import React, { useCallback, useEffect, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import { describeSlotError } from './SlotPicker';
import { WEEKDAYS, addDaysIso, formatDateLabel, todayIso } from '../../utils/appointmentLogic';
import {
  conflictHeadline, newSession, sessionsPayload, slotCount, sortSessions, validateSessions,
} from '../../utils/doctorSchedule';

const errorText = (err, fallback) => err?.response?.data?.message || describeSlotError(err) || fallback;

/** The appointments a change would leave without a valid slot, and the acknowledgement that staff will reschedule them. */
const ConflictBox = ({ conflicts, acknowledged, onAcknowledge, onConfirm, onCancel, busy }) => (
  <div className="apt-state apt-state-warn" role="alert">
    <p><strong>{conflictHeadline(conflicts.length)}</strong></p>
    <ul className="apt-plain-list">
      {conflicts.slice(0, 20).map((c) => (
        <li key={c.appointmentId}>
          <span><strong>{c.appointmentNo}</strong> · {c.pName} · {formatDateLabel(c.appointmentDate.slice(0, 10))} {c.slotStart} <em className="apt-muted">({c.reason})</em></span>
        </li>
      ))}
      {conflicts.length > 20 && <li className="apt-muted">and {conflicts.length - 20} more</li>}
    </ul>
    <label className="apt-inline-label">
      <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} /> I understand: staff will reschedule these appointments.
    </label>
    <div className="apt-actions">
      <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" disabled={!acknowledged || busy} onClick={onConfirm}>Save anyway</button>
      <button type="button" className="apt-btn apt-btn-sm" disabled={busy} onClick={onCancel}>Go back</button>
    </div>
  </div>
);

/**
 * A doctor is bookable only with a schedule. Changing a schedule or adding leave never moves or cancels an appointment: the server returns the
 * ones that no longer fit, this panel shows them, and the change is saved only after staff acknowledge them.
 */
export const DoctorSchedulePanel = ({ centreId, doctors, onChanged }) => {
  const [doctorId, setDoctorId] = useState('');
  const [rows, setRows] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [leave, setLeave] = useState({ from: todayIso(), to: todayIso(), reason: '' });
  const [pending, setPending] = useState(null);      // { kind: 'schedule' | 'leave', conflicts }
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async (id) => {
    if (!id) { setRows([]); setLeaves([]); return; }
    setError('');
    try {
      const [sched, lv] = await Promise.all([appointmentService.doctorSchedule(id, centreId), appointmentService.doctorLeaves(centreId, id)]);
      setRows(sched?.success ? sortSessions(sched.data?.rows || []) : []);
      setLeaves(lv?.success ? lv.data || [] : []);
    } catch (err) {
      setError(errorText(err, 'Could not load the schedule.'));
    }
  }, [centreId]);

  useEffect(() => { setPending(null); setAcknowledged(false); setNotice(''); load(doctorId); }, [doctorId, load]);

  const setRow = (i, patch) => setRows((list) => list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const done = async (message) => {
    setPending(null); setAcknowledged(false); setNotice(message);
    await load(doctorId);
    onChanged?.();
  };

  const saveSchedule = async (acknowledge) => {
    const problem = validateSessions(rows);
    if (problem) { setError(problem); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await appointmentService.saveDoctorSchedule({ centreId: Number(centreId), doctorId: Number(doctorId), rows: sessionsPayload(rows), acknowledgeConflicts: acknowledge });
      if (!res?.success) { setError(res?.message || 'Could not save the schedule.'); return; }
      if (res.data.saved) await done(rows.length === 0 ? 'This doctor can no longer be booked.' : 'Schedule saved.');
      else { setPending({ kind: 'schedule', conflicts: res.data.conflicts || [] }); setAcknowledged(false); }
    } catch (err) {
      setError(errorText(err, 'Could not save the schedule.'));
    } finally { setBusy(false); }
  };

  const addLeave = async (acknowledge) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await appointmentService.addDoctorLeave({
        centreId: Number(centreId), doctorId: Number(doctorId), fromDate: leave.from, toDate: leave.to, reason: leave.reason.trim() || undefined, acknowledgeConflicts: acknowledge,
      });
      if (!res?.success) { setError(res?.message || 'Could not save the leave.'); return; }
      if (res.data.saved) await done('Leave saved.');
      else { setPending({ kind: 'leave', conflicts: res.data.conflicts || [] }); setAcknowledged(false); }
    } catch (err) {
      setError(errorText(err, 'Could not save the leave.'));
    } finally { setBusy(false); }
  };

  const removeLeave = async (l) => {
    setBusy(true); setError('');
    try {
      const res = await appointmentService.deleteDoctorLeave(l.leaveId);
      if (res?.success) await done('Leave removed.'); else setError(res?.message || 'Could not remove the leave.');
    } catch (err) {
      setError(errorText(err, 'Could not remove the leave.'));
    } finally { setBusy(false); }
  };

  return (
    <>
      <p className="apt-hint">Only doctors with a schedule can be booked, one patient per slot. A centre holiday or closed weekday still applies.</p>
      <div className="apt-field">
        <label className="apt-label" htmlFor="apt-doc-pick">Doctor</label>
        <select id="apt-doc-pick" className="apt-input" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
          <option value="">Choose a doctor</option>
          {(doctors || []).map((d) => <option key={d.doctorId} value={d.doctorId}>{d.doctorName}</option>)}
        </select>
      </div>

      {error && <p className="apt-error-text" role="alert">{error}</p>}
      {notice && <p className="apt-hint" role="status">{notice}</p>}

      {doctorId && (
        <>
          <h3 className="apt-subhead">Weekly sessions</h3>
          {rows.length === 0 && <p className="apt-muted">No sessions yet: this doctor cannot be booked.</p>}
          {rows.map((r, i) => (
            <div className="apt-form-row" key={i}>
              <select className="apt-input apt-input-sm" aria-label="Weekday" value={r.weekday} onChange={(e) => setRow(i, { weekday: Number(e.target.value) })}>
                {WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <input type="time" className="apt-input apt-input-sm" aria-label="Start time" value={r.startTime} onChange={(e) => setRow(i, { startTime: e.target.value })} />
              <input type="time" className="apt-input apt-input-sm" aria-label="End time" value={r.endTime} onChange={(e) => setRow(i, { endTime: e.target.value })} />
              <label className="apt-inline-label">Slot (min)
                <input type="number" min="5" max="240" className="apt-input apt-input-sm" aria-label="Slot length in minutes" value={r.slotMinutes} onChange={(e) => setRow(i, { slotMinutes: e.target.value })} style={{ width: 70 }} />
              </label>
              <span className="apt-muted">{slotCount(r)} slots</span>
              <button type="button" className="apt-btn apt-btn-sm apt-btn-danger" onClick={() => setRows((list) => list.filter((_, idx) => idx !== i))} aria-label="Remove session">Remove</button>
            </div>
          ))}
          <div className="apt-actions">
            <button type="button" className="apt-btn apt-btn-sm" onClick={() => setRows((list) => [...list, newSession(list.length ? list[list.length - 1].weekday : 1)])}>Add session</button>
            <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" disabled={busy} onClick={() => saveSchedule(false)}>Save schedule</button>
          </div>

          <h3 className="apt-subhead">Leave</h3>
          <div className="apt-form-row">
            <label className="apt-inline-label">From <input type="date" className="apt-input apt-input-sm" min={addDaysIso(todayIso(), -366)} value={leave.from} onChange={(e) => setLeave((l) => ({ ...l, from: e.target.value, to: l.to < e.target.value ? e.target.value : l.to }))} /></label>
            <label className="apt-inline-label">To <input type="date" className="apt-input apt-input-sm" min={leave.from} value={leave.to} onChange={(e) => setLeave((l) => ({ ...l, to: e.target.value }))} /></label>
            <input className="apt-input" maxLength={150} placeholder="Reason, e.g. conference" aria-label="Leave reason" value={leave.reason} onChange={(e) => setLeave((l) => ({ ...l, reason: e.target.value }))} />
            <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" disabled={busy || !leave.from || !leave.to} onClick={() => addLeave(false)}>Add leave</button>
          </div>
          <ul className="apt-plain-list">
            {leaves.length === 0 && <li className="apt-muted">No leave recorded.</li>}
            {leaves.map((l) => (
              <li key={l.leaveId}>
                <span>{formatDateLabel(l.fromDate.slice(0, 10))} to {formatDateLabel(l.toDate.slice(0, 10))}{l.reason ? ` · ${l.reason}` : ''}</span>
                <button type="button" className="apt-btn apt-btn-sm apt-btn-danger" disabled={busy} onClick={() => removeLeave(l)} aria-label="Remove leave">Remove</button>
              </li>
            ))}
          </ul>

          {pending && (
            <ConflictBox conflicts={pending.conflicts} acknowledged={acknowledged} onAcknowledge={setAcknowledged} busy={busy}
              onCancel={() => { setPending(null); setAcknowledged(false); }}
              onConfirm={() => (pending.kind === 'schedule' ? saveSchedule(true) : addLeave(true))} />
          )}
        </>
      )}
    </>
  );
};

export default DoctorSchedulePanel;
